import React from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import {
  buildVehicleTracks,
  trailWithin,
  vehiclePositionAt,
  type ActivityTimes,
  type PlaybackSegmentInput,
} from '../../../../utils/simulation/vehiclePlayback';

const PANE_NAME = 'vehiclePlayback';
// Above road paths (440), below markerPane (600) so stop markers stay clickable.
const PANE_Z_INDEX = 450;
const TAIL_MS = 40 * 60 * 1000;
const MAX_ONBOARD_SCALE = 6;

export interface VehiclePlaybackLayerProps {
  segments: PlaybackSegmentInput[];
  activityTimes: ActivityTimes;
  timeMs: number;
}

// ponytail: one raw canvas, not L.Polyline per piece; per-frame redraw of 1000+ trails needs it.
function VehiclePlaybackLayer({ segments, activityTimes, timeMs }: VehiclePlaybackLayerProps) {
  const map = useMap();
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const timeMsRef = React.useRef(timeMs);
  timeMsRef.current = timeMs;
  const tracks = React.useMemo(
    () => buildVehicleTracks(segments, activityTimes),
    [segments, activityTimes]
  );

  const draw = React.useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const timeMs = timeMsRef.current;
    const size = map.getSize();
    const ratio = window.devicePixelRatio || 1;
    // Round: canvas dimensions are integers, so a fractional DPR would never match and resize every frame.
    const width = Math.round(size.x * ratio);
    const height = Math.round(size.y * ratio);
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
      canvas.style.width = `${size.x}px`;
      canvas.style.height = `${size.y}px`;
    }
    // The pane moves with the map while panning; pin the canvas to the viewport's top-left.
    L.DomUtil.setPosition(canvas, map.containerPointToLayerPoint([0, 0]));
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.clearRect(0, 0, size.x, size.y);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    const toPx = (p: [number, number]) => map.latLngToContainerPoint(p);

    tracks.forEach((track) => {
      const { color } = track;
      ctx.strokeStyle = color;
      track.segments.forEach((segment) => {
        const { points, times } = trailWithin(segment, timeMs, TAIL_MS);
        if (points.length < 2) return;
        ctx.lineWidth = 2 + Math.min(segment.onboardCount, MAX_ONBOARD_SCALE);
        let prev = toPx(points[0]);
        for (let i = 1; i < points.length; i += 1) {
          const next = toPx(points[i]);
          ctx.globalAlpha = Math.max(0, 1 - (timeMs - times[i]) / TAIL_MS) * 0.9;
          ctx.beginPath();
          ctx.moveTo(prev.x, prev.y);
          ctx.lineTo(next.x, next.y);
          ctx.stroke();
          prev = next;
        }
      });
      const vehicle = vehiclePositionAt(track, timeMs);
      if (!vehicle) return;
      const px = toPx(vehicle.position);
      const load = Math.min(vehicle.onboardCount, MAX_ONBOARD_SCALE);
      ctx.globalAlpha = 1;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(px.x, px.y, 4 + load, 0, Math.PI * 2);
      ctx.fill();
      ctx.lineWidth = 2;
      ctx.strokeStyle = '#ffffff';
      ctx.stroke();
    });
    ctx.globalAlpha = 1;
  }, [map, tracks]);

  React.useEffect(() => {
    const pane = map.getPane(PANE_NAME) ?? map.createPane(PANE_NAME);
    pane.style.zIndex = String(PANE_Z_INDEX);
    pane.style.pointerEvents = 'none'; // must never swallow marker clicks
    const canvas = L.DomUtil.create('canvas', 'leaflet-zoom-hide', pane);
    canvasRef.current = canvas;
    return () => {
      canvasRef.current = null;
      L.DomUtil.remove(canvas);
    };
  }, [map]);

  React.useEffect(() => {
    map.on('move resize', draw);
    return () => {
      map.off('move resize', draw);
    };
  }, [map, draw]);

  React.useEffect(draw, [draw, timeMs]);

  return null;
}

export default React.memo(VehiclePlaybackLayer);
